import { EventSourcePolyfill } from 'event-source-polyfill';
import {
  SparseFormattedFlashcard,
  SparseFormattedVignette,
} from '../types/Lecture';

export interface NameDatePayload {
  name: string;
  date: string;
}

export interface MappedContentPayload {
  vignettes: SparseFormattedVignette[];
  flashcards: SparseFormattedFlashcard[];
}

type StreamContentInput = { type: string; value: string; examDate?: Date };

type OnNameDateCallback = (data: NameDatePayload) => void;
type OnMappedCallback = (data: MappedContentPayload) => void;
type OnErrorCallback = (e: Event) => void;

export function streamContentMapping(
  input: StreamContentInput,
  onNameDate: OnNameDateCallback,
  onMapped: OnMappedCallback,
  onError?: OnErrorCallback,
  timeoutMs: number = 300000 // 5 minutes
): EventSource {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('No auth token found for SSE request.');
    throw new Error('Authentication token is missing.');
  }

  const baseUrl = import.meta.env.VITE_BACKEND_URL;
  let queryString = '';

  if (input.type === 'text') {
    if (input.value.length > 5000) {
      console.warn('Input text might be too long for a GET request URL.');
    }
    queryString = `text=${encodeURIComponent(input.value)}`;
  } else if (input.type === 'fileKey') {
    queryString = `fileKey=${encodeURIComponent(input.value)}`;
  } else {
    throw new Error('Invalid input type provided for streamContentMapping.');
  }

  if (input.examDate) {
    const dateObject = parseAndValidateDate(input.examDate);
    // Only append the parameter if we have a valid Date object
    if (dateObject) {
      queryString += `&examDate=${encodeURIComponent(dateObject.toISOString())}`;
    }
  }

  const path = `/ai/map-content-sse?${queryString}`;
  const url = `${baseUrl}${path}`;

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const es = new EventSourcePolyfill(url, {
    headers,
    heartbeatTimeout: 900000, // 10 minutes
  });

  // Set up timeout
  const timeoutId = setTimeout(() => {
    console.error('SSE connection timed out after', timeoutMs, 'ms');
    es.close();
    onError?.(
      new ErrorEvent('timeout', {
        message: `Connection timed out after ${timeoutMs}ms`,
      })
    );
  }, timeoutMs);

  es.addEventListener('name-date', (evt: MessageEvent) => {
    try {
      onNameDate(JSON.parse(evt.data));
    } catch (parseError) {
      console.error(
        'Failed to parse name-date data:',
        parseError,
        'Raw data:',
        evt.data
      );
      clearTimeout(timeoutId);
      es.close();
      onError?.(
        new ErrorEvent('parseerror', {
          message: 'Failed to parse name-date JSON',
        })
      );
    }
  });

  es.addEventListener('mapped-content', (evt: MessageEvent) => {
    try {
      onMapped(JSON.parse(evt.data));
    } catch (parseError) {
      console.error(
        'Failed to parse mapped-content data:',
        parseError,
        'Raw data:',
        evt.data
      );
      onError?.(
        new ErrorEvent('parseerror', {
          message: 'Failed to parse mapped-content JSON',
        })
      );
    } finally {
      clearTimeout(timeoutId);
      es.close();
    }
  });

  es.addEventListener('error', (evt: MessageEvent) => {
    console.error('SSE error', evt);
    clearTimeout(timeoutId);
    es.close();
    onError?.(evt);
  });

  es.onerror = (evt: Event) => {
    console.error('SSE error', evt);
    clearTimeout(timeoutId);
    es.close();
    onError?.(evt);
  };

  return es;
}

// Helper function to parse and validate date input
function parseAndValidateDate(dateInput: unknown): Date | null {
  if (!dateInput) {
    return null;
  }

  if (dateInput instanceof Date) {
    // Check if the date instance itself is valid
    if (!isNaN(dateInput.getTime())) {
      return dateInput;
    } else {
      console.warn('Received an invalid Date object.');
      return null;
    }
  }

  if (typeof dateInput === 'string' || typeof dateInput === 'number') {
    const parsedDate = new Date(dateInput);
    // Check if parsing resulted in a valid date
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    } else {
      console.warn(`Could not parse date from value: ${dateInput}.`);
      return null;
    }
  }

  // Log warning for unexpected types
  console.warn(`Received unexpected type for date: ${typeof dateInput}.`);
  return null;
}
